/**
 * 采样管理 / 按合同 / 任务监控 — 对齐 Ivan PickAdmin / TaskTrack 数据形态
 */
import api from '@/utils/api'
import { fetchPickRows, IVAN_INSPECT_SUB_TYPES, applyOverridesToRows } from '@/utils/pickAdapter'

/** App 已提交待审列表 */
export async function fetchPendingPickItems() {
  try {
    const res = await api.get('yn_pick_pending')
    return (res.data && res.data.items) || []
  } catch (e) {
    return []
  }
}

/** 已审完（样品交接）列表 */
export async function fetchAuditedPickItems(xmbh) {
  try {
    const q = xmbh ? { xmbh } : {}
    const res = await api.get('yn_pick_audited', q)
    return (res.data && res.data.items) || []
  } catch (e) {
    return []
  }
}

/** 待审孤儿任务（如 item-soil-test）补成 PC 行 */
function rowFromPending(it) {
  const tid = String((it && it.taskId) || '')
  const xmbh = String((it && it.xmbh) || '')
  return {
    id: tid || ('pending-' + xmbh),
    itemId: tid,
    xmbh,
    projectCode: xmbh,
    xmmc: (it && it.title) || xmbh,
    caseName: (it && it.title) || xmbh,
    inspectSubType: (it && it.inspectType) || '',
    inspectItems: (it && it.itemNames) || '',
    monitorPlace: (it && it.place) || '',
    pickCharge: (it && it.submitter_xm) || '',
    pickMembers: '',
    pickStaff: (it && it.submitter_xm) || '',
    pickDateLabel: '',
    frequency: '',
    status: '待审核',
    pickStatus: 'pending_audit',
    selected: false,
    fromPending: true,
    updatedAt: (it && it.updatedAt) || ''
  }
}

function rowFromAudited(it) {
  const row = rowFromPending(it)
  row.status = '已完成采样'
  row.pickStatus = 'audited'
  row.fromPending = false
  row.fromAudited = true
  return row
}

/**
 * 把 yn_pick_pending 映到监测项行；只按 taskId 精确匹配。
 * 对不上的待审记录追加一行（禁止按「同类型」误绑，否则会打开 item-64 却拉不到 App 的 item-soil-test）
 */
export function applyPendingToPickRows(pickRows, pendingItems) {
  const rows = (pickRows || []).map(r => ({ ...r }))
  const byId = {}
  rows.forEach(r => {
    if (r && r.id) byId[String(r.id)] = r
  })
  ;(pendingItems || []).forEach(it => {
    if (!it) return
    const tid = String(it.taskId || '')
    const xmbh = String(it.xmbh || '')
    const hit = tid ? byId[tid] : null
    if (hit) {
      // 已审过的行不要再被待审覆盖
      if (hit.pickStatus === 'audited') return
      hit.pickStatus = 'pending_audit'
      hit.status = '待审核'
      hit.pickRecordTaskId = tid
      if (it.submitter_xm && !hit.pickCharge) hit.pickCharge = it.submitter_xm
      if (it.place && !hit.monitorPlace) hit.monitorPlace = it.place
      if (it.itemNames && !hit.inspectItems) hit.inspectItems = it.itemNames
    } else if (xmbh || tid) {
      const syn = rowFromPending(it)
      syn.pickRecordTaskId = tid
      rows.push(syn)
      if (syn.id) byId[String(syn.id)] = syn
    }
  })
  return rows
}

/** 已审 → 样品交接；覆盖同 taskId 的待审态 */
export function applyAuditedToPickRows(pickRows, auditedItems) {
  const rows = (pickRows || []).map(r => ({ ...r }))
  const byId = {}
  rows.forEach(r => {
    if (r && r.id) byId[String(r.id)] = r
  })
  ;(auditedItems || []).forEach(it => {
    if (!it) return
    const tid = String(it.taskId || '')
    const xmbh = String(it.xmbh || '')
    const hit = tid ? byId[tid] : null
    if (hit) {
      hit.pickStatus = 'audited'
      hit.status = '已完成采样'
      hit.pickRecordTaskId = tid
      if (it.submitter_xm && !hit.pickCharge) hit.pickCharge = it.submitter_xm
      if (it.place && !hit.monitorPlace) hit.monitorPlace = it.place
      if (it.itemNames && !hit.inspectItems) hit.inspectItems = it.itemNames
    } else if (xmbh || tid) {
      const syn = rowFromAudited(it)
      syn.pickRecordTaskId = tid
      rows.push(syn)
      if (syn.id) byId[String(syn.id)] = syn
    }
  })
  // 待审核置顶，其次已完成采样
  rows.sort((a, b) => {
    const rank = (r) => {
      if (!r) return 9
      if (r.pickStatus === 'pending_audit' || r.status === '待审核') return 0
      if (r.pickStatus === 'audited' || r.status === '已完成采样' || r.status === '样品交接') return 1
      return 2
    }
    return rank(a) - rank(b)
  })
  return rows
}

export function projectTypeLabel(xm) {
  const fm = String((xm && xm.flow_model) || '')
  const jclx = String((xm && (xm.jclx || xm.jclb)) || '')
  if (fm === 'RoutineEntrustFlow' || /自行|自测/.test(jclx)) {
    if (/企业自测|企业自检/.test(jclx)) return '企业自测'
    return '自行监测'
  }
  if (fm === 'SelfSendEntrustFlow') return '来样检测'
  if (/企业自测|企业自检/.test(jclx)) return '企业自测'
  return '委托检测'
}

export function groupDateLabel(dateStr) {
  if (!dateStr) return '更早以前'
  const d = String(dateStr).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return '更早以前'
  const today = new Date()
  const pad = n => String(n).padStart(2, '0')
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  if (d === todayStr) return '今天'
  const y = new Date(today.getTime() - 86400000)
  const yStr = `${y.getFullYear()}-${pad(y.getMonth() + 1)}-${pad(y.getDate())}`
  if (d === yStr) return '昨天'
  return '更早以前'
}

export function formatListDate(dateStr) {
  const s = String(dateStr || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s || ''
  const today = new Date()
  const pad = n => String(n).padStart(2, '0')
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  if (s === todayStr) return `${s.slice(5, 7)}月${s.slice(8, 10)}日`
  // 跨年显示完整，同年显示 MM-DD（Ivan dateFormat:auto 近似）
  if (s.slice(0, 4) === String(today.getFullYear())) return s
  return s
}

/** Ivan WfCaseState：优先突出待审 */
export function wfCaseStateText(pickRows) {
  const rows = pickRows || []
  const n = rows.length
  if (!n) return '待安排采样'
  const audit = rows.filter(r => r.pickStatus === 'pending_audit' || r.status === '待审核').length
  if (audit > 0) return `${audit}条待审核`
  const pending = rows.filter(r => r.status === '待安排采样' || !r.pickDateFrom).length
  const doing = rows.filter(r => r.status === '采样中' || r.status === '已提交').length
  if (pending === n) return `${n}条待安排采样`
  if (doing > 0) return `${n}条采样进行中`
  return `${n}条采样进行中`
}

/** 任务监控左侧节点摘要（与工作台 xmzt 对齐） */
export function actListLabel(pickRows, xm) {
  const zt = String((xm && xm.xmzt) || '').toLowerCase()
  if (zt === 'labing' || zt === 'lab' || zt === 'analysing') return ['实验室分析']
  if (zt === 'lab_ready') return ['检测任务安排']
  if (zt === 'reporting' || zt === 'report') return ['报告打印']
  if (zt === 'sampling' || zt === 'doing' || zt === 'dispatched' || zt === 'handover') return ['采样任务管理']
  const rows = pickRows || []
  if (!rows.length) return ['待安排采样']
  if (rows.every(r => r.status === '待安排采样')) return ['采样任务管理']
  return ['采样任务管理']
}

export function buildProjectViews(pickRows) {
  const byXm = {}
  ;(pickRows || []).forEach(r => {
    const k = r.xmbh
    if (!k) return
    if (!byXm[k]) byXm[k] = { rows: [], xm: r.rawXm || { xmbh: k, xmmc: r.xmmc } }
    byXm[k].rows.push(r)
    if (r.rawXm) byXm[k].xm = r.rawXm
  })

  return Object.keys(byXm).map(xmbh => {
    const { rows, xm } = byXm[xmbh]
    const date = xm.gxsj || xm.cjsj || xm.entrust_date || rows[0].pickDateFrom || ''
    return {
      xmbh,
      ProjectName: xm.xmmc || xmbh,
      ProjectDate: date,
      ProjectCode: xmbh,
      ProjectType: projectTypeLabel(xm),
      WfCaseState: wfCaseStateText(rows),
      ActList: actListLabel(rows, xm),
      CreateDate: date,
      pickRows: rows,
      rawXm: xm
    }
  }).sort((a, b) => String(b.ProjectDate).localeCompare(String(a.ProjectDate)))
}

export function groupProjectViews(views) {
  const order = ['今天', '昨天', '更早以前']
  const map = {}
  ;(views || []).forEach(v => {
    const k = groupDateLabel(v.ProjectDate || v.CreateDate)
    if (!map[k]) map[k] = []
    map[k].push(v)
  })
  return order.filter(k => map[k] && map[k].length).map(k => ({
    key: k,
    expand: true,
    items: map[k]
  }))
}

export async function fetchDoingPickBundle() {
  const [rawRows, pendingItems, auditedItems] = await Promise.all([
    fetchPickRows(),
    fetchPendingPickItems(),
    fetchAuditedPickItems()
  ])
  let pickRows = applyPendingToPickRows(rawRows, pendingItems)
  pickRows = applyAuditedToPickRows(pickRows, auditedItems)
  applyOverridesToRows(pickRows)
  const projectViews = buildProjectViews(pickRows)
  const typeCount = {}
  IVAN_INSPECT_SUB_TYPES.forEach(t => { typeCount[t] = 0 })
  pickRows.forEach(r => {
    const t = r.inspectSubType || '其他'
    typeCount[t] = (typeCount[t] || 0) + 1
  })
  const total = pickRows.length || 1
  const typeShare = Object.keys(typeCount)
    .filter(k => typeCount[k] > 0)
    .map(k => ({ name: k, value: typeCount[k], pct: Math.round((typeCount[k] / total) * 1000) / 10 }))

  const staffMap = {}
  pickRows.forEach(r => {
    String(r.pickStaff || r.pickCharge || '')
      .split(/[;；,，]/)
      .map(s => s.trim())
      .filter(Boolean)
      .forEach(name => {
        if (!staffMap[name]) staffMap[name] = { name, pickCount: 0, sampleCount: 0 }
        staffMap[name].pickCount += 1
        staffMap[name].sampleCount += 1
      })
  })

  return {
    pickRows,
    projectViews,
    groups: groupProjectViews(projectViews),
    pickTaskCount: pickRows.length,
    projectTaskCount: projectViews.length,
    typeShare,
    staffWorkload: Object.values(staffMap).sort((a, b) => b.pickCount - a.pickCount).slice(0, 12)
  }
}

/** 年度按月任务量（用项目创建月近似） */
export function yearMonthStats(projectViews) {
  const year = new Date().getFullYear()
  const created = Array(12).fill(0)
  const done = Array(12).fill(0)
  ;(projectViews || []).forEach(v => {
    const d = String(v.CreateDate || v.ProjectDate || '').slice(0, 10)
    if (!d.startsWith(String(year))) return
    const m = parseInt(d.slice(5, 7), 10) - 1
    if (m < 0 || m > 11) return
    created[m] += 1
    const zt = String((v.rawXm && v.rawXm.xmzt) || '').toLowerCase()
    if (zt === 'done') done[m] += 1
  })
  return { created, done }
}

export { fetchPickRows, IVAN_INSPECT_SUB_TYPES }
